/**
 * original source: https://ui.shadcn.com/docs/components/dialog
 */
import * as React from 'react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { cn, tv } from '@joyful/utils'
import { XIcon } from 'lucide-react'
import { IconButton } from './Button'
import { Slottable } from './Slot'
import { VisuallyHidden } from './VisuallyHidden'
import { isSonnerInteractionTarget } from '../utils/isSonnerInteractionTarget'
import type { VariantProps } from '@joyful/utils'
export type DialogProps = React.ComponentProps<typeof DialogPrimitive.Root>
const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const dialogOverlayVariants = tv({
  base: 'fixed inset-0 z-40',

  defaultVariants: {
    animation: 'default',
    variant: 'dark',
  },

  variants: {
    animation: {
      default:
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
      none: '',
    },
    variant: {
      dark: 'bg-black/80',
      light: 'bg-tremor-background/80',
    },
  },
})

type DialogOverlayProps = React.ComponentProps<typeof DialogPrimitive.Overlay> &
  VariantProps<typeof dialogOverlayVariants>

function DialogOverlay({
  animation,
  className,
  ref,
  variant,
  ...props
}: DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        dialogOverlayVariants({
          animation,
          variant,
        }),
        className,
      )}
      {...props}
    />
  )
}

const dialogContentVariants = tv({
  base: 'fixed left-[50%] z-40 flex w-full translate-x-[-50%] flex-col border border-tremor-border bg-tremor-background dark:bg-tremor-background-muted shadow-lg sm:rounded-lg',

  defaultVariants: {
    animation: 'default',
    positioning: 'center',
    size: 'default',
  },

  variants: {
    animation: {
      default:
        'duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
      none: '',
    },
    positioning: {
      center: 'top-[50%] translate-y-[-50%]',
      top: 'top-[15%] translate-y-0',
    },
    size: {
      default: 'max-h-[90dvh] max-w-lg',
      fullscreen:
        'h-dvh max-h-none w-dvw max-w-none rounded-none sm:rounded-none',
      lg: 'max-h-[90dvh] max-w-3xl overflow-hidden',
      wide: 'max-h-[90dvh] max-w-6xl overflow-hidden',
      xl: 'h-[90dvh] w-[90dvw] max-w-none overflow-hidden',
    },
  },
})

export interface DialogContentProps
  extends
    React.ComponentProps<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  dialogOverlayProps?: Partial<DialogOverlayProps>

  /**
   * @default false
   */
  shouldHideCloseButton?: boolean

  /**
   * @default false
   */
  shouldHideOverlay?: boolean
}

function DialogContent({
  animation,
  children,
  className,
  dialogOverlayProps,
  onInteractOutside,
  onPointerDownOutside,
  positioning,
  ref,
  shouldHideCloseButton = false,
  shouldHideOverlay = false,
  size,
  ...props
}: DialogContentProps) {
  const handleInteractOutside: DialogContentProps['onInteractOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onInteractOutside?.(event)
  }

  const handlePointerDownOutside: DialogContentProps['onPointerDownOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onPointerDownOutside?.(event)
  }

  return (
    <DialogPortal>
      {shouldHideOverlay ? null : (
        <DialogOverlay animation={animation} {...dialogOverlayProps} />
      )}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          dialogContentVariants({
            animation,
            positioning,
            size,
          }),
          className,
        )}
        {...props}
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handlePointerDownOutside}
      >
        <Slottable>{children}</Slottable>
        {shouldHideCloseButton ? null : (
          <div className="absolute top-4 right-4">
            <DialogPrimitive.Close asChild>
              <IconButton variant="ghost" tooltip="Close">
                <XIcon className="size-4" />
              </IconButton>
            </DialogPrimitive.Close>
          </div>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

const dialogHeaderVariants = tv({
  base: 'flex flex-col gap-y-1.5 p-6 pb-4 text-center sm:text-left',
  variants: {
    hasDivider: {
      true: 'border-b border-tremor-border',
    },
  },
})

function DialogHeader({
  className,
  hasDivider,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof dialogHeaderVariants>) {
  return (
    <div
      className={cn(
        dialogHeaderVariants({
          hasDivider,
        }),
        className,
      )}
      {...props}
    />
  )
}

export type DialogBodyProps = React.ComponentProps<'div'>

function DialogBody({ children, className, ...props }: DialogBodyProps) {
  return (
    <div
      className={cn('scrollable-y min-h-0 flex-1 px-6 py-4', className)}
      {...props}
    >
      {props['aria-description'] === undefined ? null : (
        <VisuallyHidden asChild>
          <DialogDescription>{props['aria-description']}</DialogDescription>
        </VisuallyHidden>
      )}

      {children}
    </div>
  )
}

const dialogFooterVariants = tv({
  base: 'flex flex-col-reverse p-6 pt-4 md:flex-row md:justify-end gap-x-2 gap-y-1.5',

  variants: {
    hasDivider: {
      true: 'border-t border-tremor-border',
    },
  },
})

function DialogFooter({
  className,
  hasDivider,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof dialogFooterVariants>) {
  return (
    <div
      className={cn(
        dialogFooterVariants({
          hasDivider,
        }),
        className,
      )}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-tremor-content text-sm', className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogTrigger,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
